import React from 'react';

import { SpinnerOverlay } from '@joinrepublic/dls-ui-kit';
import { observer } from 'mobx-react-lite';

import { IEventsOrderEnum } from '@r-client/data/graphql-types';
import { QueryError } from '@r-client/republic/feature/errors';
import { Error as DefaultErrorPage } from '@r-client/shared/data/error-reporting';
import { Divider, Text } from '@r-client/shared/ui/core';

import { GetEventDetailsQuery } from '../../graphql/get-event-details-query';
import { useGetEventsQuery } from '../../graphql/get-events-query';
import { EventDetailsContainer } from '../event-details/event-details-container';
import { EventList } from '../event-list/event-list';

export interface IEventDetailsContentProps {
  eventQuery: GetEventDetailsQuery;
}

export const EventDetailsContent = observer<IEventDetailsContentProps>(
  function EventDetailsContent({ eventQuery }) {
    const upcomingEventsQuery = useGetEventsQuery({
      variables: {
        timeFilters: {
          future: true,
        },
        first: 5,
        order: IEventsOrderEnum.BeginAtAsc,
      },
      notifyOnNetworkStatusChange: true,
    });

    const loadMoreUpcomingEvents = () => {
      const endCursor = upcomingEventsQuery.data?.events.pageInfo?.endCursor;
      if (!endCursor) return;
      upcomingEventsQuery.fetchMore({
        variables: { after: endCursor },
      });
    };

    if (eventQuery.isLoading) return <SpinnerOverlay solidBackground />;
    if (eventQuery.isRejected) {
      return <QueryError queries={[eventQuery]} />;
    }
    if (!eventQuery.data?.event) return <DefaultErrorPage statusCode={404} />;

    const upcomingEventsExist = !!upcomingEventsQuery.data?.events.edges.length;

    return (
      <>
        <EventDetailsContainer event={eventQuery.data.event} />

        {upcomingEventsExist && (
          <>
            <Divider margin={{ top: '4', bottom: '1.5' }} />

            <EventList
              title={
                <Text color="gray70" type="h5" as="h3">
                  Next events
                </Text>
              }
              loading={upcomingEventsQuery.isLoading}
              events={upcomingEventsQuery.data.events.edges.map(
                (edge) => edge.node
              )}
              pageInfo={upcomingEventsQuery.data.events.pageInfo}
              onLoadMore={loadMoreUpcomingEvents}
            />
          </>
        )}
      </>
    );
  }
);
